import { makeValuesPredicate } from '@sgme/fp';
import { FormattedMessage } from 'react-intl';

import ActionButtons from '@/App/BulkOrder/ActionButtons.tsx';
import { FixingOrderTable } from '@/App/BulkOrder/BulkOrderTables/FixingOrderTable.tsx';
import { StopLossOrderTable } from '@/App/BulkOrder/BulkOrderTables/StopLossOrderTable.tsx';
import { checkIfUserCanTrade, useUserContext } from '@/context/UserContext.tsx';
import { fixingBulkExecutionStatusSelector, stopLossBulkExecutionStatusSelector } from '@/store/bulkOrder/selectors.ts';
import { BulkOrderStatus } from '@/store/bulkOrder/state.ts';
import { useAppSelector } from '@/store/hooks.ts';
import { selectActiveTabId } from '@/store/tabs/slice.ts';

type BulkOrderTablesProps = {
  allStopLossBulkOrderIds: string[] | undefined;
  allFixingBulkOrderIds: string[] | undefined;
  isImportingWithNoRight: 'fixing' | 'stopLoss' | undefined;
  setStep: (step: number) => void;
  setHasErrorImporting: (hasErrorImporting: boolean) => void;
};

const isExecutedState = makeValuesPredicate([BulkOrderStatus.SUCCESS, BulkOrderStatus.FAILED]);

const BulkOrderTables = ({
  allFixingBulkOrderIds,
  allStopLossBulkOrderIds,
  isImportingWithNoRight,
  setHasErrorImporting,
  setStep,
}: BulkOrderTablesProps) => {
  const userContextValue = useUserContext();
  const tabId = useAppSelector(selectActiveTabId);

  const stopLossExecutionStatus = useAppSelector((state) => stopLossBulkExecutionStatusSelector(state, tabId));
  const fixingExecutionStatus = useAppSelector((state) => fixingBulkExecutionStatusSelector(state, tabId));
  const userPreferences = useAppSelector((state) => state.userPreferences);
  const isReadOnly = !checkIfUserCanTrade(userContextValue);

  const defaultEmails = userPreferences.splitNotifications
    ? userPreferences.splitNotificationsEmailsOrders
    : userPreferences.emails;

  return (
    <>
      <StopLossOrderTable
        orderIds={allStopLossBulkOrderIds}
        isImportingWithNoStopLossRight={isImportingWithNoRight === 'stopLoss'}
        tabId={tabId}
      />
      <FixingOrderTable
        orderIds={allFixingBulkOrderIds}
        isImportingWithNoFixingRight={isImportingWithNoRight === 'fixing'}
        tabId={tabId}
      />

      {(isExecutedState(stopLossExecutionStatus) || isExecutedState(fixingExecutionStatus)) && (
        <div className="mt-2" data-e2e="emails-notified">
          <FormattedMessage id="import.emails.notifications" />
          <p>{defaultEmails.join(', ')}</p>
        </div>
      )}

      <ActionButtons
        isReadOnly={isReadOnly}
        stopLossExecutionStatus={stopLossExecutionStatus}
        fixingExecutionStatus={fixingExecutionStatus}
        setStep={setStep}
        setHasErrorImporting={setHasErrorImporting}
        allFixingBulkOrderIds={allFixingBulkOrderIds}
        allStopLossBulkOrderIds={allStopLossBulkOrderIds}
      />
    </>
  );
};

export default BulkOrderTables;
